import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { CardModule } from 'primeng/card';
import { MessageService } from 'primeng/api';
import { forkJoin, of } from 'rxjs';
import { SupplierSiteService } from '../supplierSite.service';
import { SuppliersService } from '../../suppliers.service';
import { SupplierSite, Supplier } from '../supplierSite.model';
import { Business } from '../../../business/business.model';
import { BusinessService } from '../../../business/business.service';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputGroupModule } from 'primeng/inputgroup';
import { IftaLabelModule } from 'primeng/iftalabel';
import { EntityDropdownPickerComponent } from '../../../../shared/components/entity-dropdown-picker/entity-dropdown-picker.component';
import { EntityDropdownPickerAdapter } from '../../../../shared/components/entity-dropdown-picker/entity-dropdown-picker.model';

@Component({
  selector: 'app-supplierSite-form',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    ToastModule,
    CardModule,
    InputGroupModule,
    InputGroupAddonModule,
    IftaLabelModule,
    EntityDropdownPickerComponent,
  ],
  templateUrl: './form-supplierSite.component.html',
  providers: [MessageService],
})
export class FormSupplierSiteComponent implements OnInit {
  supplierSiteId: number | null = null;
  supplierSiteForm!: FormGroup;
  isEditMode = false;
  pageTitle = 'Create New Supplier Site';

  supplierPickerAdapter!: EntityDropdownPickerAdapter<Supplier>;
  businessPickerAdapter!: EntityDropdownPickerAdapter<Business>;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private supplierSiteService: SupplierSiteService,
    private businessService: BusinessService,
    private messageService: MessageService,
    private suppliersService: SuppliersService
  ) {}

  ngOnInit(): void {
    this.supplierPickerAdapter = this.suppliersService.createSupplierPickerAdapter();
    this.businessPickerAdapter = this.businessService.createBusinessPickerAdapter();

    this.initForm();

    this.route.paramMap.subscribe((params) => {
      const id = params.get('id');
      if (id) {
        this.supplierSiteId = +id;
        this.isEditMode = true;
        this.pageTitle = 'Edit Supplier Site';
        this.loadSupplierSiteData(this.supplierSiteId);
      }
    });
  }

  initForm(): void {
    this.supplierSiteForm = this.fb.group({
      supplierSiteName: ['', Validators.required],
      supplier: [null, Validators.required],
      business: [null, Validators.required],
      supplierSiteNumber: [''],
      erpSupplierSiteId: [''],
    });
  }

  loadSupplierSiteData(id: number): void {
    this.supplierSiteService.getSupplierSiteById(id).subscribe({
      next: (supplierSite) => {
        const supplier$ = supplierSite.supplier?.id
          ? this.suppliersService.getFreshSupplierById(supplierSite.supplier.id)
          : of(supplierSite.supplier ?? null);
        const business$ = supplierSite.business?.id
          ? this.businessService.getFreshBusinessById(supplierSite.business.id)
          : of(supplierSite.business ?? null);

        forkJoin([supplier$, business$]).subscribe({
          next: ([supplier, business]) => {
            this.supplierSiteForm.patchValue({
              ...supplierSite,
              supplier,
              business,
            });
          },
          error: (err) => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to load Supplier Site relations.',
            });
            console.error(err);
          },
        });
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load Supplier Site data.',
        });
        console.error(err);
      },
    });
  }

  saveSupplierSite(): void {
    if (this.supplierSiteForm.invalid) {
      this.supplierSiteForm.markAllAsTouched();
      return;
    }

    const formValue = this.supplierSiteForm.value;
    const supplierSiteData: SupplierSite = {
      supplierSiteId: this.supplierSiteId ?? 0,
      ...formValue,
      supplier: formValue.supplier ? { id: formValue.supplier.id } as Supplier : undefined,
      business: formValue.business ? { id: formValue.business.id } as Business : undefined,
    };

    const operation = this.isEditMode
      ? this.supplierSiteService.updateSupplierSite(supplierSiteData)
      : this.supplierSiteService.createSupplierSite(supplierSiteData);

    operation.subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: `Supplier Site ${this.isEditMode ? 'updated' : 'created'} successfully.`,
        });
        setTimeout(
          () =>
            this.router.navigate([
              '/ai-studio/session/current',
              { outlets: { canvasOutlet: ['supplier-site', 'list'] } },
            ]),
          500
        );
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: `Failed to ${this.isEditMode ? 'update' : 'create'} supplier site.`,
        });
        console.error(err);
      },
    });
  }

  cancel(): void {
    this.router.navigate([
      '/ai-studio/session/current',
      { outlets: { canvasOutlet: ['supplier-site', 'list'] } },
    ]);
  }
}
